import { useQuery } from '@tanstack/react-query'
import {
  ApiRequestError,
  getCustomerSalesOrderSettings,
  getWorkflowSalesOrderSettings,
} from '@/services/api'
import type { SalesOrderSettings } from '@/types/salesOrderSettings'

export const workflowSalesOrderSettingsQueryKey = (workflowId: string) =>
  ['workflow-sales-order-settings', workflowId] as const

export const customerSalesOrderSettingsQueryKey = (workflowId: string, customerId: string) =>
  ['customer-sales-order-settings', workflowId, customerId] as const

/** Cached for the session, the way the order confirmation settings are: they change rarely. */
export function useWorkflowSalesOrderSettings(workflowId: string | undefined) {
  return useQuery({
    queryKey: workflowSalesOrderSettingsQueryKey(workflowId ?? ''),
    queryFn: () => getWorkflowSalesOrderSettings(workflowId!),
    enabled: !!workflowId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * A customer's own settings, or null when they have none.
 *
 * The backend answers 404 for a customer that follows the workflow's settings, which is an answer
 * rather than a failure — so it is turned into null here instead of an error state.
 */
export function useCustomerSalesOrderSettings(
  workflowId: string | undefined,
  customerId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: customerSalesOrderSettingsQueryKey(workflowId ?? '', customerId ?? ''),
    queryFn: async (): Promise<SalesOrderSettings | null> => {
      try {
        return await getCustomerSalesOrderSettings(workflowId!, customerId!)
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: !!workflowId && !!customerId && enabled,
    staleTime: 60 * 1000,
  })
}
